import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import {
  BRAND, Icon, Button, Field, Input, Card, Stepper, Frame,
} from '../../../components/onboarding/primitives';
import { onboardingAPI } from '../../../services/api';
import { SAUDI_CITIES } from '../../../data/saudi-cities';

const STEP_LABELS = ['الحساب', 'المختبر', 'الفروع', 'الباقة', 'الأجهزة', 'الدفع'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2MB

/**
 * Props contract:
 *   state, setState, goToStep, goBack
 */
export default function Step2LabInfo({ state, setState, goToStep, goBack }) {
  const d = state.s2 || {};
  const set = (k, v) => setState({ ...state, s2: { ...d, [k]: v } });

  const [citySearch, setCitySearch] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const cityRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) setCityOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const vatOk = !d.vatNumber || /^3\d{13}3$/.test(d.vatNumber);
  const crOk = !d.crNumber || /^\d{10}$/.test(d.crNumber);
  const postalOk = !d.postalCode || /^\d{5}$/.test(d.postalCode);

  const canNext = !!(
    d.vatNumber && d.crNumber
    && d.street && d.buildingNumber && d.district && d.city && d.postalCode
    && vatOk && crOk && postalOk
  );

  const handleSubmit = async () => {
    if (!canNext || submitting) return;
    setSubmitting(true);

    // Pull nameAr/name from step 1 to echo back on profile update.
    const s1 = state.s1 || {};

    try {
      await onboardingAPI.updateProfile({
        nameAr: s1.nameAr,
        name: s1.name,
        vatNumber: d.vatNumber,
        crNumber: d.crNumber,
        address: {
          street: d.street,
          district: d.district,
          buildingNumber: d.buildingNumber,
          additionalNumber: d.additionalNumber || undefined,
          city: d.city,
          postalCode: d.postalCode,
        },
        supportEmail: d.supportEmail || undefined,
        phoneNumber: d.publicPhone || undefined,
      });

      if (d.logo) {
        try {
          await onboardingAPI.updateBranding({ logoUrl: d.logo });
        } catch (brandErr) {
          // Non-fatal: let the user continue even if logo upload fails.
          // eslint-disable-next-line no-console
          console.warn('[Step2] updateBranding failed:', brandErr?.message);
          toast.error('تعذّر حفظ الشعار، يمكنك رفعه لاحقاً');
        }
      }

      toast.success('تم حفظ بيانات المختبر');
      goToStep(3);
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.error
        || 'تعذّر حفظ بيانات المختبر. حاول مرة أخرى.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onBack = () => { if (goBack) goBack(); else goToStep(1); };

  return (
    <Frame
      maxWidth={820}
      stepper={<Stepper steps={STEP_LABELS} current={1} />}
      footer={
        <>
          <Button variant="outline" onClick={onBack} iconLeft={<Icon.Arrow dir="right" />}>السابق</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canNext || submitting}
            icon={submitting ? <Icon.Clock size={16} /> : <Icon.Arrow />}
          >
            {submitting ? 'جارٍ الحفظ...' : 'التالي'}
          </Button>
        </>
      }
    >
      <Card elevated padding={40}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.5 }}>
          بيانات المختبر الرسمية
        </h1>
        <p style={{ fontSize: 14.5, color: BRAND.muted, margin: '0 0 28px' }}>
          نستخدمها على الفواتير ولوحة المختبر. يمكنك تعديلها لاحقاً.
        </p>

        <Field label="شعار المختبر" optional>
          <LogoDropzone value={d.logo} onChange={(v) => set('logo', v)} />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
          <Field
            label="الرقم الضريبي (VAT)"
            required
            hint="15 رقم، يبدأ بـ 3 وينتهي بـ 3"
            error={d.vatNumber && !vatOk ? 'رقم ضريبي غير صالح' : null}
          >
            <Input
              value={d.vatNumber}
              onChange={(v) => set('vatNumber', v.replace(/\D/g, '').slice(0, 15))}
              placeholder="300000000000003"
              ltr
            />
          </Field>
          <Field
            label="رقم السجل التجاري"
            required
            error={d.crNumber && !crOk ? 'عشرة أرقام' : null}
          >
            <Input
              value={d.crNumber}
              onChange={(v) => set('crNumber', v.replace(/\D/g, '').slice(0, 10))}
              placeholder="1010000000"
              ltr
            />
          </Field>
        </div>

        <div style={{
          fontSize: 12, fontWeight: 600, color: BRAND.muted,
          textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 14px',
        }}>
          العنوان الوطني
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
          <Field label="اسم الشارع" required>
            <Input value={d.street} onChange={(v) => set('street', v)} placeholder="شارع الملك فهد" />
          </Field>
          <Field label="الحي" required>
            <Input value={d.district} onChange={(v) => set('district', v)} placeholder="العليا" />
          </Field>
          <Field label="رقم المبنى" required>
            <Input
              value={d.buildingNumber}
              onChange={(v) => set('buildingNumber', v.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000"
              ltr
              maxLength={4}
            />
          </Field>
          <Field label="الرقم الإضافي" optional>
            <Input
              value={d.additionalNumber}
              onChange={(v) => set('additionalNumber', v.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000"
              ltr
              maxLength={4}
            />
          </Field>
          {/* City picker — the dropdown sits OUTSIDE the <Field> wrapper.
              Field renders as a <label>, and a click on a list item nested
              inside a <label> triggers a synthetic click on the contained
              <input>, which bubbles to the input's onClick and re-opens the
              dropdown (race with the option's onClose). Moving the dropdown
              out of the label avoids that label-forwarding behavior entirely.
              The wrapping ref div stays position:relative so the dropdown's
              `top: 100%` still anchors against the input row. */}
          <div ref={cityRef} style={{ position: 'relative' }}>
            <Field label="المدينة" required>
              <div onMouseDown={() => setCityOpen(true)} style={{ cursor: 'pointer' }}>
                <Input
                  value={cityOpen ? citySearch : (d.city || '')}
                  onChange={(v) => { setCitySearch(v); setCityOpen(true); }}
                  placeholder={d.city ? d.city : 'ابحث عن المدينة'}
                  rightSlot={
                    <svg
                      width="12" height="8" viewBox="0 0 12 8" fill="none"
                      style={{ transition: 'transform 0.18s ease', transform: cityOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path d="M1 1l5 5 5-5" stroke={BRAND.muted} strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  }
                />
              </div>
            </Field>
            {cityOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: '#fff', border: `1px solid ${BRAND.border}`,
                borderRadius: 12, marginTop: 4, maxHeight: 220, overflowY: 'auto',
                zIndex: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              }}>
                {(() => {
                  const filtered = SAUDI_CITIES.filter((c) => !citySearch || c.includes(citySearch));
                  if (!filtered.length) {
                    return (
                      <div style={{ padding: '14px', fontSize: 13, color: BRAND.muted, textAlign: 'center' }}>
                        لا توجد نتائج مطابقة
                      </div>
                    );
                  }
                  return filtered.map((c) => {
                    const selected = c === d.city;
                    return (
                      <div
                        key={c}
                        // mousedown fires before document-level mousedown handler & before
                        // any synthetic label click — sets the city and closes the
                        // dropdown deterministically.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          set('city', c);
                          setCityOpen(false);
                          setCitySearch('');
                        }}
                        style={{
                          padding: '10px 14px', fontSize: 14, cursor: 'pointer',
                          borderBottom: `1px solid ${BRAND.surfaceAlt}`,
                          background: selected ? BRAND.mint : 'transparent',
                          fontWeight: selected ? 600 : 400,
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                        }}
                        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = BRAND.mint; }}
                        onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span>{c}</span>
                        {selected && (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2 7.5l3 3 7-7" stroke={BRAND.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
          <Field
            label="الرمز البريدي"
            required
            error={d.postalCode && !postalOk ? '5 أرقام' : null}
          >
            <Input
              value={d.postalCode}
              onChange={(v) => set('postalCode', v.replace(/\D/g, '').slice(0, 5))}
              placeholder="12345"
              ltr
              maxLength={5}
            />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px', marginTop: 8 }}>
          <Field label="إيميل الدعم (للعملاء)" optional>
            <Input value={d.supportEmail} onChange={(v) => set('supportEmail', v)} placeholder="support@lab.com" ltr />
          </Field>
          <Field label="رقم هاتف المختبر (للعملاء)" optional>
            <Input value={d.publicPhone} onChange={(v) => set('publicPhone', v)} placeholder="0112345678" ltr />
          </Field>
        </div>
      </Card>
    </Frame>
  );
}

// ─── Logo dropzone ──────────────────────────────────────
function LogoDropzone({ value, onChange }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  const handle = (file) => {
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('حجم الشعار أكبر من 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.onerror = () => toast.error('تعذّر قراءة الملف');
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files[0]); }}
        style={{
          width: 120, height: 120, borderRadius: '50%',
          border: `2px dashed ${drag ? BRAND.green : BRAND.border}`,
          background: drag ? BRAND.mint : BRAND.surfaceAlt,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', position: 'relative', overflow: 'hidden', flexShrink: 0,
        }}
      >
        {value ? (
          <>
            <img
              src={value}
              alt="logo preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
              style={{
                position: 'absolute', top: 6, right: 6,
                width: 24, height: 24, borderRadius: 12,
                background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon.X />
            </button>
          </>
        ) : (
          <div style={{ textAlign: 'center', color: BRAND.muted }}>
            <Icon.Upload size={20} />
            <div style={{ fontSize: 11, marginTop: 4 }}>رفع الشعار</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => handle(e.target.files[0])}
        />
      </div>
      <div style={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.6 }}>
        PNG, JPG أو SVG<br />
        حد أقصى 2MB · يظهر على الفواتير والموقع
      </div>
    </div>
  );
}
